import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DisplayableDomainItem } from 'reportViewer'
import LinkButton from 'components/button/LinkButton'
import FilterBar from 'components/filters/FilterBar'
import EvidenceFilters from 'components/filters/evidence/EvidenceFilter'
import FilterChips from 'components/filters/FilterChips'
import {
    filterEvidences,
    getDriverMatchKeys,
    getRelationMatchKeys
} from 'utils/evidenceFilterUtil'
import { useEvidenceFilterContext } from 'context/EvidenceFilterContext'
import { useDataContext } from 'context/DataContext'
import Box from '@mui/material/Box'
import { useEvidenceListStyles } from './EvidencePage.styles'
import EvidenceItem from './EvidenceItem'

type Props = {
    displayableDomainItem: DisplayableDomainItem | null
    isNegative: boolean
    isMenuOpen: boolean
}

export const EvidenceList: React.FC<Props> = ({
    displayableDomainItem,
    isNegative
}) => {
    const [listOpen, setListOpen] = useState(false)
    const styles = useEvidenceListStyles()
    const { t } = useTranslation('evidence')

    const evidence = useMemo(
        () => displayableDomainItem?.evidence || [],
        [displayableDomainItem?.evidence]
    )

    const { tumor, drivers } = useDataContext()

    const {
        exactMatchers,
        handleExactMatcherFilterChange,
        selectedResearchType,
        setSelectedResearchType
    } = useEvidenceFilterContext()

    const filteredEvidences = useMemo(() => {
        const matchTumor = exactMatchers.includes('MATCH_TUMOR')
        const matchProfile = exactMatchers.includes('MATCH_PROFILE')

        const researchTypeForFilter =
            selectedResearchType === '' ? undefined : selectedResearchType

        const patientTumor = matchTumor ? tumor : undefined

        const filterParams = selectedResearchType
            ? { researchType: researchTypeForFilter }
            : {}

        const molecularProfileMatches = matchProfile
            ? new Set(
                  drivers
                      .flatMap((driver) => getDriverMatchKeys(driver))
                      .filter(Boolean)
              )
            : undefined

        const matchingEvidences = filterEvidences(
            evidence,
            filterParams,
            patientTumor,
            molecularProfileMatches
        )

        return matchingEvidences
            .map((ev) => {
                const matchingRelations = ev.relations.filter((relation) => {
                    const relationKeys = getRelationMatchKeys(relation)

                    return molecularProfileMatches
                        ? relationKeys.some((key) =>
                              molecularProfileMatches.has(key)
                          )
                        : true
                })

                return {
                    ...ev,
                    relations: matchingRelations
                }
            })
            .filter((ev) => ev.relations.length > 0)
    }, [exactMatchers, selectedResearchType, tumor, drivers, evidence])

    return (
        <div className={styles.listContainer}>
            <div
                className={`${styles.evidenceListHeader} ${
                    isNegative ? 'negative' : ''
                }`}
            >
                <div className={styles.title}>
                    {t('evidenceList')}
                    <span className={`${styles.listCounter}`}>
                        {filteredEvidences.length}
                    </span>
                </div>

                {evidence.length > 0 && (
                    <Box className={styles.evidenceFilterBarContainer}>
                        <FilterBar
                            filter={<EvidenceFilters />}
                            chips={
                                <FilterChips
                                    chips={[
                                        {
                                            key: 'research-type',
                                            items: selectedResearchType
                                                ? [selectedResearchType]
                                                : [],
                                            labelKey: (v) => `filter.${v}`,
                                            onDelete: () =>
                                                setSelectedResearchType(''),
                                            showDivider: true
                                        },
                                        {
                                            key: 'exact-matchers',
                                            items: exactMatchers,
                                            labelKey: (v) => `filter.${v}`,
                                            onDelete:
                                                handleExactMatcherFilterChange,
                                            showDivider: true
                                        }
                                    ]}
                                />
                            }
                            layout="reversed"
                            showBarContainer={false}
                        />
                    </Box>
                )}

                {filteredEvidences.length > 0 && (
                    <Box>
                        <LinkButton
                            label={t('showAllRelations')}
                            genomateThemed
                            shrinked
                            onClick={() => setListOpen(!listOpen)}
                            className={styles.evidenceListShowAllButton}
                        />
                    </Box>
                )}
            </div>

            <div className={styles.evidenceListContent}>
                {filteredEvidences.length > 0 ? (
                    filteredEvidences.map((value, idx) => (
                        <EvidenceItem
                            listOpen={listOpen}
                            key={idx}
                            evidenceItem={value}
                            idx={idx}
                        />
                    ))
                ) : (
                    <EvidenceItem
                        listOpen={false}
                        evidenceItem={null}
                        idx={0}
                    />
                )}
            </div>
        </div>
    )
}

export default EvidenceList